import type { Payout } from '../types/payout';
import { filterByYear } from './payoutFilters';
import { calculateTotalBNCAmount } from './payoutCalculations';

export interface MonthInfo {
  key: string;
  label: string;
  year: number;
  month: number;
}

export interface ChartDataPoint {
  month: string;
  value: number;
}

export interface PlusValueDataPoint {
  month: string;
  plusValue: number;
  positiveValue: number | null;
  negativeValue: number | null;
  acquisitionCost: number;
  currentValue: number;
}

export interface TokenData {
  crypto: {
    id: string;
    symbol: string;
    name: string;
  };
  totalAmount: number;
  currentPrice: number;
  currentTotalValue: number;
  averagePurchasePrice: number;
  totalValueAtPurchase: number;
}

export interface TreemapDataPoint {
  name: string;
  symbol: string;
  value: number;
  percentage: number;
  // Present only on the "Autres" cell: per-token breakdown, used to expand it on demand
  others?: TreemapDataPoint[];
}

// Above this many individual tokens, the smallest ones are grouped into a single "Autres" cell.
// The user can still expand that cell to see every token individually.
export const TREEMAP_MAX_SLOTS = 12;

// Calculate average payouts per month
export function calculateAveragePayoutsPerMonth(payouts: Payout[]): number {
  if (payouts.length === 0) return 0;

  const monthsSet = new Set<string>();
  payouts.forEach(p => {
    const date = new Date(p.date);
    const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
    monthsSet.add(monthKey);
  });

  return payouts.length / monthsSet.size;
}

// Calculate average payout value
export function calculateAverageValue(payouts: Payout[]): number {
  if (payouts.length === 0) return 0;
  const total = payouts.reduce((sum, p) => sum + (p.totalValue || 0), 0);
  return total / payouts.length;
}

export interface YearlyBNCSummary {
  bncTotal: number; // Revenus payout/stacking de l'année, à déclarer au BNC
  estimatedTax: number; // Estimation de la taxe URSSAF (bncTotal * bncRate)
  taxableSalesTotal: number; // Total des ventes de l'année (cessions déclenchant la plus-value)
}

// Calculate the yearly BNC revenue, its estimated URSSAF tax, and the total of taxable
// sales (cessions), for a given year (defaults to the current year)
export function calculateYearlyBNCSummary(
  payouts: Payout[],
  bncRate: number = 0.34,
  year: number = new Date().getFullYear()
): YearlyBNCSummary {
  const yearPayouts = filterByYear(payouts, year);

  const bncTotal = yearPayouts.reduce((sum, p) => {
    return p.type === 'payout' || p.type === 'stacking' ? sum + (p.totalValue || 0) : sum;
  }, 0);

  const taxableSalesTotal = yearPayouts.reduce((sum, p) => {
    return p.type === 'vente' ? sum + (p.totalValue || 0) : sum;
  }, 0);

  return {
    bncTotal,
    estimatedTax: calculateTotalBNCAmount(yearPayouts, bncRate),
    taxableSalesTotal
  };
}

// Helper: Get last N months
export function getLastMonths(count: number): MonthInfo[] {
  const months: MonthInfo[] = [];
  const now = new Date();

  for (let i = count - 1; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      key: `${date.getFullYear()}-${date.getMonth()}`,
      label: date.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }),
      year: date.getFullYear(),
      month: date.getMonth()
    });
  }

  return months;
}

// Prepare chart data for portfolio value (last N months)
export function preparePortfolioChartData(
  payouts: Payout[],
  tokens: TokenData[],
  monthsCount: number
): ChartDataPoint[] {
  const months = getLastMonths(monthsCount);

  // Create a map for quick token lookup by crypto id
  const tokenMap = new Map<string, TokenData>();
  tokens.forEach(token => {
    tokenMap.set(token.crypto.id, token);
  });

  return months.map(monthInfo => {
    // Get all payouts up to and including this month
    // EXCLUDE 'vente' (sales) as they reduce holdings, not increase them
    const payoutsUpToMonth = payouts.filter(p => {
      const payoutDate = new Date(p.date);
      const payoutKey = `${payoutDate.getFullYear()}-${payoutDate.getMonth()}`;
      // Only include acquisitions (payout, achat, stacking), not disposals (vente)
      return payoutKey <= monthInfo.key && p.type !== 'vente' && p.type !== 'swap';
    });

    // Calculate cumulative holdings by crypto
    const holdingsByToken = new Map<string, number>();
    payoutsUpToMonth.forEach(p => {
      if (p.crypto && p.amount !== undefined) {
        const currentAmount = holdingsByToken.get(p.crypto.id) || 0;
        holdingsByToken.set(p.crypto.id, currentAmount + p.amount);
      }
    });

    // Calculate total value with current prices
    let totalValue = 0;
    holdingsByToken.forEach((amount, cryptoId) => {
      const token = tokenMap.get(cryptoId);
      if (token && token.currentPrice) {
        totalValue += amount * token.currentPrice;
      }
    });

    return {
      month: monthInfo.label,
      value: totalValue
    };
  });
}

// Prepare plus-value chart data
export function preparePlusValueChartData(
  payouts: Payout[],
  tokens: TokenData[],
  monthsCount: number
): PlusValueDataPoint[] {
  const months = getLastMonths(monthsCount);

  // Create a map for quick token lookup by crypto id
  const tokenMap = new Map<string, TokenData>();
  tokens.forEach(token => {
    tokenMap.set(token.crypto.id, token);
  });

  return months.map(monthInfo => {
    // Get all payouts up to and including this month
    // EXCLUDE 'vente' (sales) as they reduce holdings, not increase them
    const payoutsUpToMonth = payouts.filter(p => {
      const payoutDate = new Date(p.date);
      const payoutKey = `${payoutDate.getFullYear()}-${payoutDate.getMonth()}`;
      // Only include acquisitions (payout, achat, stacking), not disposals (vente)
      return payoutKey <= monthInfo.key && p.type !== 'vente' && p.type !== 'swap';
    });

    // Calculate cumulative holdings and acquisition cost by crypto
    const holdingsByToken = new Map<string, { amount: number; acquisitionCost: number }>();
    payoutsUpToMonth.forEach(p => {
      if (p.crypto && p.amount !== undefined && p.totalValue !== undefined) {
        const current = holdingsByToken.get(p.crypto.id) || { amount: 0, acquisitionCost: 0 };
        holdingsByToken.set(p.crypto.id, {
          amount: current.amount + p.amount,
          acquisitionCost: current.acquisitionCost + p.totalValue
        });
      }
    });

    // Calculate total current value and total acquisition cost
    let totalCurrentValue = 0;
    let totalAcquisitionCost = 0;

    holdingsByToken.forEach((data, cryptoId) => {
      const token = tokenMap.get(cryptoId);
      if (token && token.currentPrice) {
        totalCurrentValue += data.amount * token.currentPrice;
      }
      totalAcquisitionCost += data.acquisitionCost;
    });

    const plusValue = totalCurrentValue - totalAcquisitionCost;

    return {
      month: monthInfo.label,
      plusValue: plusValue,
      positiveValue: plusValue > 0 ? plusValue : null,
      negativeValue: plusValue < 0 ? plusValue : null,
      acquisitionCost: totalAcquisitionCost,
      currentValue: totalCurrentValue
    };
  });
}

// Prepare treemap data: current holdings by token, sorted by value.
// Tokens beyond TREEMAP_MAX_SLOTS are grouped into a single "Autres" cell so the chart
// stays readable regardless of how many distinct cryptos the user holds.
export function prepareTreemapData(tokens: TokenData[]): TreemapDataPoint[] {
  const validTokens = tokens.filter(token => token.currentTotalValue > 0);
  const totalValue = validTokens.reduce((sum, token) => sum + token.currentTotalValue, 0);

  if (totalValue === 0) return [];

  const sorted = [...validTokens].sort((a, b) => b.currentTotalValue - a.currentTotalValue);
  const topTokens = sorted.slice(0, TREEMAP_MAX_SLOTS);
  const remainingTokens = sorted.slice(TREEMAP_MAX_SLOTS);

  const result: TreemapDataPoint[] = topTokens.map(token => ({
    name: token.crypto.name,
    symbol: token.crypto.symbol.toUpperCase(),
    value: token.currentTotalValue,
    percentage: (token.currentTotalValue / totalValue) * 100
  }));

  if (remainingTokens.length > 0) {
    const othersValue = remainingTokens.reduce((sum, token) => sum + token.currentTotalValue, 0);
    const others: TreemapDataPoint[] = remainingTokens.map(token => ({
      name: token.crypto.name,
      symbol: token.crypto.symbol.toUpperCase(),
      value: token.currentTotalValue,
      percentage: (token.currentTotalValue / totalValue) * 100
    }));
    result.push({
      name: 'Autres',
      symbol: `${remainingTokens.length} tokens`,
      value: othersValue,
      percentage: (othersValue / totalValue) * 100,
      others
    });
  }

  return result;
}
